import {CancellablePromise} from '@yandex-data-ui/sdk';

import {
    ComOrganizationList,
    ComOrgSource,
    ComService,
    ComActiveOrganization,
    ComFederation,
} from '~shared/models';

import {getRedirectURL, redirectToContextUrl, getSafeRedirectUrl} from '~ui/utils';

import {sdk} from '~ui/services/sdk';

const COM_ID_SIZE = 36;
const COM_ID_PATTERN = [8, 4, 4, 4, 12];

export function getOrgSource(id: string) {
    if (!id) {
        return undefined;
    }

    if (id.length === COM_ID_SIZE) {
        const parts = id.split('-');
        if (
            parts.length === COM_ID_PATTERN.length &&
            parts.every((part, index) => part.length === COM_ID_PATTERN[index])
        ) {
            return 'com';
        }
    }

    if (Number.isInteger(Number(id))) {
        return 'dir';
    }

    return 'cloud';
}

export function isServiceEnable(organization: ComActiveOrganization, name: ComService) {
    if (!organization.enabled_services || !organization.enabled_services.length) {
        return false;
    }

    return organization.enabled_services.some((service: ComService) => service === name);
}

export function getFederationOrgId(federations: ComFederation[], source: ComOrgSource) {
    return federations.find(({identity: {src}}) => src === source)?.identity?.id;
}

export function changeContext(organizations: ComOrganizationList, orgId: string, sk: string) {
    const redirectUrl = getRedirectURL();
    const source = getOrgSource(orgId);
    const {active} = organizations;

    if (source === 'com') {
        const organization = active.find(({collab_id: collabId}) => collabId === orgId);

        if (!organization) {
            return new CancellablePromise(Promise.reject('No organization'));
        }

        const directoryOrgId = getFederationOrgId(organization.federations, ComOrgSource.Dir);
        const cloudOrgId = getFederationOrgId(organization.federations, ComOrgSource.Cloud);

        if (!isServiceEnable(organization, ComService.Wiki)) {
            return sdk.com
                .enableService({orgId: organization.collab_id, serviceSlug: ComService.Wiki})
                .then(() => {
                    return redirectToContextUrl(
                        sk,
                        {collabOrgId: organization.collab_id, directoryOrgId, cloudOrgId},
                        redirectUrl,
                    );
                });
        }

        redirectToContextUrl(
            sk,
            {collabOrgId: organization.collab_id, directoryOrgId, cloudOrgId},
            redirectUrl,
        );

        return new CancellablePromise(Promise.resolve());
    }

    // need activate
    return sdk.com
        .activateOrganization({
            identity: {src: source as ComOrgSource, id: orgId},
            hybrid: true,
        })
        .then((res) => {
            const {collab_id: collabOrgId, federations} = res;
            const directoryOrgId = getFederationOrgId(federations, ComOrgSource.Dir);
            const cloudOrgId = getFederationOrgId(federations, ComOrgSource.Cloud);

            return redirectToContextUrl(
                sk,
                {
                    collabOrgId,
                    directoryOrgId,
                    cloudOrgId,
                },
                redirectUrl,
            );
        });
}

export function createComOrganization(sk: string, force: boolean = true, displayName?: string) {
    return sdk.com
        .createOrganization({
            force,
            displayName,
        })
        .then((organization) => {
            const redirectUrl = getSafeRedirectUrl(
                new URL('/hi-there', window.location.origin).href,
            );

            return redirectToContextUrl(
                sk,
                {
                    collabOrgId: organization.collab_id,
                    directoryOrgId: getFederationOrgId(organization.federations, ComOrgSource.Dir),
                    cloudOrgId: getFederationOrgId(organization.federations, ComOrgSource.Cloud),
                },
                redirectUrl,
            );
        });
}
